import { Badge, Button, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import { DataTable, PageHeader } from "@how/ui";
import { CampusModal } from "./CampusModal";
import { useCampuses, type CampusRow } from "./campus-queries";

const columns: ColumnDef<CampusRow>[] = [
  {
    accessorKey: "name",
    header: "Campus",
    enableSorting: false,
    cell: (ctx) => (
      <Text size="sm" fw={600}>
        {ctx.row.original.name}
      </Text>
    ),
  },
  {
    accessorKey: "addressLines",
    header: "Address",
    enableSorting: false,
    cell: (ctx) =>
      ctx.row.original.addressLines.length === 0 ? (
        <Text size="sm" c="dimmed">
          No address yet
        </Text>
      ) : (
        <Text size="sm">{ctx.row.original.addressLines.join(", ")}</Text>
      ),
  },
  {
    accessorKey: "serviceTimes",
    header: "Services",
    enableSorting: false,
    meta: { numeric: true },
    cell: (ctx) => ctx.row.original.serviceTimes.length,
  },
  {
    accessorKey: "timeZone",
    header: "Time zone",
    enableSorting: false,
    cell: (ctx) =>
      ctx.row.original.timeZone === null ? (
        <Text size="sm" c="dimmed">
          The church's
        </Text>
      ) : (
        <Text size="sm">{ctx.row.original.timeZone}</Text>
      ),
  },
  { accessorKey: "peopleCount", header: "People", enableSorting: false, meta: { numeric: true } },
  {
    accessorKey: "isActive",
    header: "Status",
    enableSorting: false,
    cell: (ctx) =>
      ctx.row.original.isActive ? (
        <Badge variant="light" color="teal">
          Active
        </Badge>
      ) : (
        <Badge variant="light" color="gray">
          Archived
        </Badge>
      ),
  },
];

const PAGE = { pageIndex: 0, pageSize: 200 };

/**
 * The church's campuses (ADR 0036): where it meets. Every church has at least one, created with the church and
 * named after it, so a single-building church only ever sees one row. A few rows by nature, so the list is read
 * whole; a row opens the same dialog that adds a campus.
 */
export function CampusesPage() {
  const campuses = useCampuses();
  const canManage = usePermission("campuses.manage", { write: true });
  // undefined: closed. null: creating. A row: editing that campus.
  const [open, setOpen] = useState<CampusRow | null | undefined>(undefined);
  const status = campuses.isPending ? "loading" : campuses.isError ? "error" : "ready";

  return (
    <>
      <PageHeader
        title="Campuses"
        description="Where your church meets. Service times and addresses on your website come from here."
        actions={
          canManage.data === true ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setOpen(null);
              }}
            >
              Add campus
            </Button>
          ) : undefined
        }
      />
      <DataTable
        label="Campuses"
        columns={columns}
        rows={campuses.data ?? []}
        totalRows={campuses.data?.length ?? 0}
        status={status}
        pagination={PAGE}
        onPaginationChange={() => undefined}
        sorting={[]}
        onSortingChange={() => undefined}
        getRowId={(row) => row.id}
        onRowClick={(row) => {
          setOpen(row);
        }}
        emptyTitle="No campuses"
        emptyDescription="Every church has one. If this is empty, something is wrong; tell support."
        errorMessage="Could not load campuses."
        onRetry={() => {
          void campuses.refetch();
        }}
      />
      <CampusModal
        key={open === undefined ? "closed" : open === null ? "new" : open.id}
        campus={open}
        canManage={canManage.data === true}
        activeCount={campuses.data?.filter((c) => c.isActive).length ?? 0}
        onClose={() => {
          setOpen(undefined);
        }}
      />
    </>
  );
}
